/**
 * Geometric hit-testing and z-order selection for overlapping signing fields.
 *
 * DOM paint order (later fields in the page list render on top) is the source of
 * truth for "topmost". Selection cycling walks that stack at a viewer point.
 */

import type { SigningField } from '../types/fields';

export type ViewerRect = { top: number; left: number; width: number; height: number };

export type ViewerPoint = { x: number; y: number };

/** Per-page overlap stack touched on the previous pick (survives PdfPage remounts). */
const lastOverlapStackKeyByPage = new Map<number, string>();

export function getLastOverlapStackKey(pageIndex: number): string | null {
  return lastOverlapStackKeyByPage.get(pageIndex) ?? null;
}

export function setLastOverlapStackKey(pageIndex: number, key: string | null): void {
  if (key) {
    lastOverlapStackKeyByPage.set(pageIndex, key);
  } else {
    lastOverlapStackKeyByPage.delete(pageIndex);
  }
}

export function resetAllOverlapStackKeys(): void {
  lastOverlapStackKeyByPage.clear();
}

export function overlapStackKey(fields: SigningField[]): string {
  return fields.map((f) => f.id).join('|');
}

/** True when (x, y) lies inside the rect (inclusive edges). */
export function isPointInViewerRect(x: number, y: number, rect: ViewerRect): boolean {
  return (
    x >= rect.left &&
    x <= rect.left + rect.width &&
    y >= rect.top &&
    y <= rect.top + rect.height
  );
}

/** True when (pdfX, pdfY) lies inside the field's PDF bounding box. */
export function isPointInFieldPdf(
  pdfX: number,
  pdfY: number,
  field: SigningField
): boolean {
  return (
    pdfX >= field.x &&
    pdfX <= field.x + field.width &&
    pdfY >= field.y &&
    pdfY <= field.y + field.height
  );
}

/**
 * Fields containing the viewer point, in paint order (bottom → top).
 * The last entry is the topmost field at that point.
 */
export function getFieldsAtViewerPoint(
  pageFields: SigningField[],
  viewerX: number,
  viewerY: number,
  getRect: (field: SigningField) => ViewerRect
): SigningField[] {
  const hits: SigningField[] = [];
  for (const field of pageFields) {
    const rect = getRect(field);
    if (isPointInViewerRect(viewerX, viewerY, rect)) {
      hits.push(field);
    }
  }
  return hits;
}

/**
 * Fields containing the PDF point on a page, in paint order (bottom → top).
 */
export function getFieldsAtPdfPoint(
  fields: SigningField[],
  pageIndex: number,
  pdfX: number,
  pdfY: number
): SigningField[] {
  return fields.filter(
    (field) =>
      field.pageIndex === pageIndex && isPointInFieldPdf(pdfX, pdfY, field)
  );
}

export function getTopmostFieldAtViewerPoint(
  pageFields: SigningField[],
  viewerX: number,
  viewerY: number,
  getRect: (field: SigningField) => ViewerRect
): SigningField | null {
  const hits = getFieldsAtViewerPoint(pageFields, viewerX, viewerY, getRect);
  return hits.length > 0 ? hits[hits.length - 1] : null;
}

export type SelectAtPointOptions = {
  /** When true, advance to the next field in the overlap stack. */
  cycle?: boolean;
  /** Pixel distance threshold for treating a click as "same spot". */
  repeatThresholdPx?: number;
  lastClick?: ViewerPoint | null;
};

export type SelectAtPointResult = {
  selectedId: string | null;
  /** Updated click position to store for repeat-click detection. */
  clickPoint: ViewerPoint | null;
  /** Whether the click was interpreted as a cycle step. */
  cycled: boolean;
};

/**
 * Pick the field to select from a precomputed overlap stack at a viewer coordinate.
 * - No hits → clear selection.
 * - First click / new spot → topmost field in the stack.
 * - Repeat click at the same spot (or cycle=true) → next field in stack, wrapping.
 */
export function selectFieldAtPoint(
  hits: SigningField[],
  viewerX: number,
  viewerY: number,
  selectedFieldId: string | null,
  options: SelectAtPointOptions = {}
): SelectAtPointResult {
  const threshold = options.repeatThresholdPx ?? 8;

  if (hits.length === 0) {
    return { selectedId: null, clickPoint: null, cycled: false };
  }

  const last = options.lastClick;
  const repeat =
    !!options.cycle ||
    (!!last &&
      !!selectedFieldId &&
      Math.hypot(viewerX - last.x, viewerY - last.y) <= threshold &&
      hits.some((f) => f.id === selectedFieldId));

  let selectedId: string;

  if (repeat && selectedFieldId && hits.length > 1) {
    const idx = hits.findIndex((f) => f.id === selectedFieldId);
    if (idx !== -1) {
      selectedId = hits[(idx + 1) % hits.length].id;
      return {
        selectedId,
        clickPoint: { x: viewerX, y: viewerY },
        cycled: true,
      };
    }
  }

  selectedId = hits[hits.length - 1].id;
  return {
    selectedId,
    clickPoint: { x: viewerX, y: viewerY },
    cycled: false,
  };
}

/** Viewer-rect hit test wrapper (used by unit tests and non-PDF callers). */
export function selectFieldAtViewerPoint(
  pageFields: SigningField[],
  viewerX: number,
  viewerY: number,
  getRect: (field: SigningField) => ViewerRect,
  selectedFieldId: string | null,
  options: SelectAtPointOptions = {}
): SelectAtPointResult {
  const hits = getFieldsAtViewerPoint(pageFields, viewerX, viewerY, getRect);
  return selectFieldAtPoint(hits, viewerX, viewerY, selectedFieldId, options);
}

/**
 * Advance selection within the overlap stack at the PDF center of `currentField`.
 * Returns null when there is nothing to cycle.
 */
export function cycleFieldSelectionAtPdfCenter(
  fields: SigningField[],
  currentField: SigningField
): string | null {
  const pdfX = currentField.x + currentField.width / 2;
  const pdfY = currentField.y + currentField.height / 2;
  const hits = getFieldsAtPdfPoint(fields, currentField.pageIndex, pdfX, pdfY);

  if (hits.length <= 1) return null;

  const idx = hits.findIndex((f) => f.id === currentField.id);
  if (idx === -1) return hits[hits.length - 1].id;

  return hits[(idx + 1) % hits.length].id;
}